import { supabase } from './supabase'

export interface Lender {
  id: string
  name: string
  description: string | null
  url: string
  logo_url: string | null
  project_type: 'commercial' | 'residential' | 'both'
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface TenantLender {
  id: string
  tenant_id: string
  lender_id: string
  is_active: boolean
  display_order: number
  created_at: string
  updated_at: string
  lender?: Lender
}

export interface CreateLenderDto {
  name: string
  description?: string
  url: string
  logo_url?: string
  project_type: 'commercial' | 'residential' | 'both'
}

export interface UpdateLenderDto extends Partial<CreateLenderDto> {
  is_active?: boolean
}

class LendersService {
  // Get all lenders (global library)
  async list(): Promise<Lender[]> {
    const { data, error } = await supabase
      .from('lenders')
      .select('*')
      .order('name')

    if (error) throw error
    return data || []
  }

  // Get active lenders only
  async listActive(): Promise<Lender[]> {
    const { data, error } = await supabase
      .from('lenders')
      .select('*')
      .eq('is_active', true)
      .order('name')

    if (error) throw error
    return data || []
  }

  // Get a single lender
  async get(id: string): Promise<Lender> {
    const { data, error } = await supabase
      .from('lenders')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error
    return data
  }

  // Create a new lender
  async create(lender: CreateLenderDto): Promise<Lender> {
    const { data, error } = await supabase
      .from('lenders')
      .insert(lender)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Update a lender
  async update(id: string, updates: UpdateLenderDto): Promise<Lender> {
    const { data, error } = await supabase
      .from('lenders')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Delete a lender
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('lenders')
      .delete()
      .eq('id', id)

    if (error) throw error
  }

  // Get lenders assigned to a tenant
  async getTenantLenders(tenantId: string): Promise<TenantLender[]> {
    const { data, error } = await supabase
      .from('tenant_lenders')
      .select(`
        *,
        lender:lenders(*)
      `)
      .eq('tenant_id', tenantId)
      .order('display_order')

    if (error) throw error
    return data || []
  }

  // Assign a lender to a tenant
  async assignToTenant(tenantId: string, lenderId: string, displayOrder: number = 0): Promise<TenantLender> {
    const { data, error } = await supabase
      .from('tenant_lenders')
      .insert({
        tenant_id: tenantId,
        lender_id: lenderId,
        display_order: displayOrder,
        is_active: true
      })
      .select(`
        *,
        lender:lenders(*)
      `)
      .single()

    if (error) throw error
    return data
  }

  // Remove a lender from a tenant
  async removeFromTenant(tenantId: string, lenderId: string): Promise<void> {
    const { error } = await supabase
      .from('tenant_lenders')
      .delete()
      .eq('tenant_id', tenantId)
      .eq('lender_id', lenderId)

    if (error) throw error
  }

  // Update tenant lender settings (e.g., display order, is_active)
  async updateTenantLender(id: string, updates: { is_active?: boolean; display_order?: number }): Promise<TenantLender> {
    const { data, error } = await supabase
      .from('tenant_lenders')
      .update(updates)
      .eq('id', id)
      .select(`
        *,
        lender:lenders(*)
      `)
      .single()

    if (error) throw error
    return data
  }
}

export const lendersService = new LendersService()
